import { useId, useState } from "react";
import { ImagePlus, Link2, Loader2, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { uploadListingImage } from "@/lib/listingImages";

type UploadFieldProps = {
  label: string;
  value: string;
  folder: string;
  className?: string;
  onChange: (value: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
};

type GalleryFieldProps = {
  label: string;
  value: string[];
  folder: string;
  className?: string;
  onChange: (value: string[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
};

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : "Upload failed");

export const ImageUploadField = ({ label, value, folder, className = "", onChange, onUploadingChange }: UploadFieldProps) => {
  const inputId = useId();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    setIsUploading(true);
    onUploadingChange?.(true);

    try {
      const url = await uploadListingImage(file, folder);
      onChange(url);
      toast({ title: "Image uploaded" });
    } catch (error) {
      toast({ title: "Upload failed", description: getErrorMessage(error), variant: "destructive" });
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
    }
  };

  return (
    <div className={`space-y-3 ${className}`.trim()}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-body text-sm text-foreground">{label}</p>
        <label htmlFor={inputId} className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs text-foreground hover:bg-muted">
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {isUploading ? "Uploading..." : "Upload image"}
        </label>
        <input id={inputId} type="file" accept="image/*" className="hidden" onChange={(event) => {
          void handleFileChange(event.target.files);
          event.target.value = "";
        }} />
      </div>

      <div className="relative">
        <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          placeholder="Paste image URL or upload one"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-3 text-sm text-foreground"
        />
      </div>

      {value ? (
        <img src={value} alt={label} className="h-40 w-full rounded-lg border border-border object-cover" loading="lazy" />
      ) : (
        <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
          No image selected yet.
        </div>
      )}
    </div>
  );
};

export const ImageGalleryField = ({ label, value, folder, className = "", onChange, onUploadingChange }: GalleryFieldProps) => {
  const inputId = useId();
  const [isUploading, setIsUploading] = useState(false);
  const [manualUrl, setManualUrl] = useState("");

  const handleFilesChange = async (files: FileList | null) => {
    const selectedFiles = Array.from(files ?? []);
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    onUploadingChange?.(true);

    try {
      const uploadedUrls = await Promise.all(selectedFiles.map((file) => uploadListingImage(file, folder)));
      onChange(Array.from(new Set([...(value ?? []), ...uploadedUrls])));
      toast({ title: `${uploadedUrls.length} gallery image${uploadedUrls.length > 1 ? "s" : ""} uploaded` });
    } catch (error) {
      toast({ title: "Upload failed", description: getErrorMessage(error), variant: "destructive" });
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
    }
  };

  const addManualUrl = () => {
    const nextUrl = manualUrl.trim();
    if (!nextUrl) return;
    onChange(Array.from(new Set([...(value ?? []), nextUrl])));
    setManualUrl("");
  };

  return (
    <div className={`space-y-3 ${className}`.trim()}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-body text-sm text-foreground">{label}</p>
        <label htmlFor={inputId} className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs text-foreground hover:bg-muted">
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {isUploading ? "Uploading..." : "Upload gallery images"}
        </label>
        <input id={inputId} type="file" accept="image/*" multiple className="hidden" onChange={(event) => {
          void handleFilesChange(event.target.files);
          event.target.value = "";
        }} />
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Paste a gallery image URL"
            value={manualUrl}
            onChange={(event) => setManualUrl(event.target.value)}
            className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-3 text-sm text-foreground"
          />
        </div>
        <button type="button" onClick={addManualUrl} className="rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-muted">
          Add URL
        </button>
      </div>

      {value.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-3">
          {value.map((imageUrl) => (
            <div key={imageUrl} className="relative overflow-hidden rounded-lg border border-border">
              <img src={imageUrl} alt="Gallery preview" className="h-32 w-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => onChange(value.filter((item) => item !== imageUrl))}
                className="absolute right-2 top-2 rounded-full bg-background/90 p-1 text-destructive shadow-sm"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
          No gallery images added yet.
        </div>
      )}
    </div>
  );
};